import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { MessageCircle, CheckCircle2, AlertTriangle, Loader2 } from 'lucide-react';
import { Card, Button, FieldHint } from '../ui';
import { theme } from '../../styles/theme';
import api from '../../services/api';

/**
 * Botão de conexão de WhatsApp via Embedded Signup (Facebook Login for Business).
 *
 * Fluxo:
 *   1. Carrega o Facebook JS SDK e inicializa com REACT_APP_FB_APP_ID.
 *   2. No clique, abre FB.login com o config_id (REACT_APP_FB_WHATSAPP_CONFIG_ID),
 *      response_type='code'. Captura waba_id/phone_number_id do sessionInfo (message event).
 *   3. Envia { code, wabaId, phoneNumberId } para POST /connectors/{tenantId}/whatsapp/signup.
 *
 * Só renderiza quando a feature flag e o config_id estão presentes — assim o fluxo
 * manual de conector WhatsApp segue inalterado onde a flag está desligada.
 *
 * Props:
 *   tenantId: string              tenant alvo
 *   onConnected?: (result) => void  callback após sucesso (ex.: recarregar conectores)
 */

const FB_APP_ID       = process.env.REACT_APP_FB_APP_ID || '';
const FB_CONFIG_ID    = process.env.REACT_APP_FB_WHATSAPP_CONFIG_ID || '';
const FEATURE_ENABLED = String(process.env.REACT_APP_WHATSAPP_EMBEDDED_SIGNUP || '').toLowerCase() === 'true';
const GRAPH_VERSION   = process.env.REACT_APP_FB_GRAPH_VERSION || 'v21.0';

const Wrapper = styled(Card)`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[3]};
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
`;

const StatusBox = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  font-size: ${theme.typography.sm};
  color: ${(p) => (p.$error ? theme.colors.danger : theme.colors.success)};
`;

// Carrega o Facebook JS SDK uma única vez
function loadFbSdk() {
  return new Promise((resolve, reject) => {
    if (window.FB) return resolve(window.FB);
    if (document.getElementById('facebook-jssdk')) {
      const check = setInterval(() => {
        if (window.FB) { clearInterval(check); resolve(window.FB); }
      }, 100);
      setTimeout(() => { clearInterval(check); reject(new Error('FB SDK timeout')); }, 10000);
      return;
    }
    window.fbAsyncInit = function () {
      window.FB.init({ appId: FB_APP_ID, autoLogAppEvents: true, xfbml: false, version: GRAPH_VERSION });
      resolve(window.FB);
    };
    const js = document.createElement('script');
    js.id = 'facebook-jssdk';
    js.src = 'https://connect.facebook.net/en_US/sdk.js';
    js.onerror = () => reject(new Error('Falha ao carregar o SDK do Facebook'));
    document.body.appendChild(js);
  });
}

export default function WhatsAppConnectButton({ tenantId, onConnected }) {
  const { t } = useTranslation('connectors');
  const [status, setStatus] = useState('idle'); // idle | loading | success | error
  const [message, setMessage] = useState('');
  const sessionInfoRef = useRef({ wabaId: null, phoneNumberId: null });

  // Captura waba_id / phone_number_id do evento de sessão do Embedded Signup
  useEffect(() => {
    if (!FEATURE_ENABLED || !FB_CONFIG_ID) return undefined;
    function onMessage(event) {
      if (event.origin !== 'https://www.facebook.com' && event.origin !== 'https://web.facebook.com') return;
      try {
        const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
        // Diagnóstico temporário: mostra no console (F12) o evento do Embedded Signup
        if (data?.type === 'WA_EMBEDDED_SIGNUP') {
          // eslint-disable-next-line no-console
          console.info('[whatsapp-signup] evento do Meta:', data.event, data.data);
        }
        if (data?.type === 'WA_EMBEDDED_SIGNUP' && data?.data) {
          sessionInfoRef.current = {
            wabaId: data.data.waba_id || null,
            phoneNumberId: data.data.phone_number_id || null,
          };
        }
      } catch {
        /* evento não-JSON, ignora */
      }
    }
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  const submit = useCallback(async (code) => {
    setStatus('loading');
    setMessage(t('whatsappSignup.connecting', 'Conectando...'));
    try {
      // O evento WA_EMBEDDED_SIGNUP pode chegar logo depois do callback do login.
      // Espera até 3s pelo sessionInfo antes de enviar.
      for (let i = 0; i < 15 && !sessionInfoRef.current.wabaId; i += 1) {
        // eslint-disable-next-line no-await-in-loop
        await new Promise((r) => setTimeout(r, 200));
      }
      const { wabaId, phoneNumberId } = sessionInfoRef.current;
      // eslint-disable-next-line no-console
      console.info('[whatsapp-signup] enviando ao backend:', { wabaId, phoneNumberId });
      const res = await api.post(`/connectors/${tenantId}/whatsapp/signup`, { code, wabaId, phoneNumberId });
      setStatus('success');
      setMessage(t('whatsappSignup.success', 'WhatsApp conectado com sucesso!'));
      if (onConnected) onConnected(res.data);
    } catch (err) {
      setStatus('error');
      const apiMsg = err?.response?.data?.message;
      setMessage(apiMsg || t('whatsappSignup.error', 'Falha ao conectar o WhatsApp.'));
    }
  }, [tenantId, onConnected, t]);

  const handleConnect = useCallback(async () => {
    setStatus('loading');
    setMessage(t('whatsappSignup.opening', 'Abrindo conexão...'));
    try {
      const FB = await loadFbSdk();
      FB.login((response) => {
        const code = response?.authResponse?.code;
        if (code) {
          submit(code);
        } else {
          setStatus('idle');
          setMessage('');
        }
      }, {
        config_id: FB_CONFIG_ID,
        response_type: 'code',
        override_default_response_type: true,
        extras: { setup: {}, featureType: '', sessionInfoVersion: '3' },
      });
    } catch (err) {
      setStatus('error');
      setMessage(err.message || t('whatsappSignup.sdkError', 'Não foi possível carregar o SDK.'));
    }
  }, [submit, t]);

  // Fluxo automatizado desabilitado → não renderiza (fluxo manual segue intacto)
  if (!FEATURE_ENABLED || !FB_CONFIG_ID || !FB_APP_ID) return null;

  return (
    <Wrapper>
      <Row>
        <MessageCircle size={20} color={theme.colors.primary} />
        <strong>{t('whatsappSignup.title', 'Conectar WhatsApp')}</strong>
      </Row>
      <FieldHint>
        {t('whatsappSignup.hint', 'Conecte o número de WhatsApp da sua empresa em poucos cliques, sem configuração manual.')}
      </FieldHint>

      <div>
        <Button type="button" onClick={handleConnect} disabled={status === 'loading'}>
          {status === 'loading'
            ? <Loader2 size={16} className="spin" />
            : <MessageCircle size={16} />}
          {t('whatsappSignup.button', 'Conectar WhatsApp')}
        </Button>
      </div>

      {status === 'success' && (
        <StatusBox><CheckCircle2 size={16} /> {message}</StatusBox>
      )}
      {status === 'error' && (
        <StatusBox $error><AlertTriangle size={16} /> {message}</StatusBox>
      )}
    </Wrapper>
  );
}
